import { Plus } from "lucide-react";
import { memo, useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { UnloadingDirectionItem } from "./UnloadingDirectionItem";

import { useGetRailstations, useLoadRoad } from "@/common/api";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  SearchableCombobox,
} from "@/common/components";
import { useAuth } from "@/common/hooks";

const LIMIT = 100;
const PAGE = 1;

export const DirectionTransportationCard = memo(() => {
  const { control, setValue, watch, formState, trigger } = useFormContext();

  const watchStation = watch("loadingStation");
  const watchRoad = watch("loadingRoad");

  const { fields, append, remove } = useFieldArray({
    control,
    name: "unloadingDirection",
  });

  const {
    auth: { isCargoOwner },
  } = useAuth();

  const [stationSearch, setStationSearch] = useState("");
  const [stationOptions, setStationOptions] = useState<Record<"label" | "value", string>[]>([]);
  const [roadSearch, setRoadSearch] = useState("");
  const [roadOptions, setRoadOptions] = useState<Record<"label" | "value", string>[]>([]);

  const { data: stationsData, isLoading: isLoadingStations } = useGetRailstations({
    name: stationSearch,
    code: watchStation,
    page: PAGE,
    limit: LIMIT,
  });

  const { data: roadData, isLoading: isLoadingRoads } = useLoadRoad({
    name: roadSearch,
    code: watchRoad,
    page: PAGE,
    limit: LIMIT,
  });

  const handleClickAddDirection = () => {
    append({
      unloadingStation: "",
      unloadingRoad: "",
      countWagons: "",
      tariffStation: undefined,
      tariffRoad: undefined,
      roadCode: "",
      stationCode: "",
    });
  };

  const handleClickRemoveDirection = (index: number) => {
    remove(index);
  };

  const handleTotalWagonsChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string | number) => void,
  ) => {
    const value = e.target.value;
    onChange(value);
    trigger(["decadeFirst", "decadeSecond", "decadeThird"]);
  };

  useEffect(() => {
    setStationOptions(
      stationsData?.data.payload?.items?.map(({ name, code, road }) => {
        const roadName = road ?? "";
        const label = roadName ? `${name ?? ""} (${roadName})` : (name ?? "");
        return {
          label,
          value: code ?? "",
        };
      }) ?? [],
    );
  }, [stationsData]);

  useEffect(() => {
    setRoadOptions(
      roadData?.data.payload?.items?.map(({ name, code, fullName }) => ({
        label: fullName ?? name ?? "",
        value: code ?? "",
      })) ?? [],
    );
  }, [roadData]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Направление перевозки</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Станции погрузки */}
        <div
          className={
            isCargoOwner
              ? "grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 p-3 md:p-4 border rounded-lg"
              : "grid grid-cols-1 md:grid-cols-2 gap-4"
          }
        >
          <div>
            <FormField
              control={control}
              name="loadingStation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Станция погрузки *</FormLabel>
                  <FormControl>
                    <SearchableCombobox
                      options={stationOptions}
                      value={watchStation}
                      onValueChange={(value) => {
                        field.onChange(value);
                        const item = stationsData?.data.payload?.items?.find(
                          (item) => item.code === value,
                        );

                        setValue("loadingStation", value);
                        setValue("codeStation", value);
                        setValue("loadingRoad", item?.fullRoad?.code ?? "");

                        setStationSearch("");
                        setRoadSearch("");
                      }}
                      onSearchChange={(value) => {
                        setValue("loadingStation", "");
                        setValue("codeStation", "");
                        setValue("loadingRoad", "");

                        setStationSearch(value);
                        setRoadSearch("");
                      }}
                      placeholder="Выберите станцию погрузки"
                      searchPlaceholder="Поиск станции..."
                      emptyText="Станция не найдена"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div>
            <FormField
              control={control}
              name="codeStation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Код станции</FormLabel>
                  <Input {...field} disabled placeholder="Код станции" />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div>
            <FormField
              control={control}
              name="loadingRoad"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Дорога погрузки</FormLabel>
                  <FormControl>
                    <Input
                      ref={field.ref}
                      name={field.name}
                      value={
                        roadOptions.find((option) => option.value === field.value)?.label ?? ""
                      }
                      disabled
                      placeholder="Дорога погрузки"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {isCargoOwner && (
          <>
            <div className="space-y-3">
              <div className="p-3 md:p-4 border rounded-lg">
                {/* Общее количество вагонов - обязательное */}
                <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-2 md:gap-4 pb-3 md:pb-5">
                  <div>
                    <FormField
                      control={control}
                      name="totalWagons"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Общее количество вагонов *</FormLabel>
                          <Input
                            {...field}
                            placeholder="Кол-во вагонов"
                            type="number"
                            onChange={(e) => handleTotalWagonsChange(e, field.onChange)}
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="text-Text-secondary text-xs leading-4 place-self-start mt-1 md:mt-5">
                    При создании заявки выберите станцию выгрузки или найдите её по коду — дорога
                    выгрузки и код дороги будут добавлены в систему автоматически. Поле «Количество
                    вагонов» необязательное, а «Общее количество вагонов» — обязательное для
                    заполнения.
                  </div>
                </div>

                <div className="flex flex-col">
                  {fields?.map((field, index) => (
                    <UnloadingDirectionItem
                      key={field.id}
                      index={index}
                      onRemoveDirection={handleClickRemoveDirection}
                    />
                  ))}

                  <div className="mt-3 md:mt-4 flex justify-end">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleClickAddDirection}
                      className="text-sm md:text-base w-full md:w-auto"
                    >
                      <Plus className="block h-4 w-4 mr-1" />
                      Добавить направление <span className="hidden md:block">выгрузки</span>
                    </Button>
                  </div>

                  {formState.errors.unloadingDirection?.message && (
                    <p className="text-xs font-normal leading-4 text-destructive mt-2 text-right">
                      {String(formState.errors.unloadingDirection.message)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
});
